/** Build / hydrate paper-attempt answer payloads for the quiz API. */

export function buildPaperAnswerPayloads(userAnswers = {}, questions = []) {
  const out = [];
  (questions || []).forEach((q) => {
    const ans = userAnswers[q.id];
    if (!ans) return;
    const questionId = ans.question_db_id || q.db_id;
    if (!questionId) return;

    const payload = { question_id: String(questionId) };
    const kind = q.interaction_kind;

    if (kind === 'essay_choice_group' || q.is_essay || q.pearsonWritingItem) {
      const essayOpts = q.essay_options || [];
      const selected =
        essayOpts.find((o) => o.label === ans.label || o.id === ans.essay_option_id) ||
        essayOpts.find((o) => o.label === q.writingPromptLabel) ||
        essayOpts[0] ||
        (q.options || []).find((o) => o.label === ans.label || o.label === q.writingPromptLabel);
      const optionId =
        ans.essay_option_id ||
        selected?.id ||
        selected?.db_id ||
        selected?.db_answer_id ||
        selected?.external_option_id;
      // Visited prompts are initialised with an empty string. Sending those
      // would overwrite the one prompt the student actually wrote.
      if (optionId && String(ans.text_response || '').trim()) {
        payload.essay = {
          essay_option_id: String(optionId),
          response_text: String(ans.text_response || ''),
        };
        out.push(payload);
      }
      return;
    }

    if (kind === 'yes_no_group') {
      const rows = Array.isArray(ans.yes_no)
        ? ans.yes_no
            .filter((item) => item?.yes_no_item_id)
            .map((item) => ({
              yes_no_item_id: String(item.yes_no_item_id),
              answered_yes: Boolean(item.answered_yes),
            }))
        : Object.entries(ans.yes_no_answers || {}).map(([itemId, value]) => ({
            yes_no_item_id: String(itemId),
            answered_yes: Boolean(value),
          }));
      payload.yes_no = rows;
      out.push(payload); // an empty array is a deliberate "clear everything" signal, not "nothing to save"
      return;
    }

    if (kind === 'most_least_group') {
      const mostId = ans.most_option_id || ans.most_least_answers?.most_option_id;
      const leastId = ans.least_option_id || ans.most_least_answers?.least_option_id;
      if (mostId && leastId) {
        payload.most_least = {
          most_option_id: String(mostId),
          least_option_id: String(leastId),
        };
        out.push(payload);
      }
      return;
    }

    if (ans.answer_id) {
      payload.selected_answer_id = String(ans.answer_id);
      out.push(payload);
    }
  });
  return out;
}

export function hydrateUserAnswersFromAttempt(questions = [], savedAnswers = []) {
  const byQuestion = new Map(
    (savedAnswers || []).map((row) => [String(row.question_id), row])
  );
  const map = {};

  (questions || []).forEach((q) => {
    const qid = q.db_id ? String(q.db_id) : null;
    if (!qid) return;
    const saved = byQuestion.get(qid);
    if (!saved) return;

    if (saved.essay) {
      const essayOpts = q.essay_options || q.options || [];
      const opt = essayOpts.find(
        (o) =>
          String(o.id) === String(saved.essay.essay_option_id) ||
          String(o.db_answer_id) === String(saved.essay.essay_option_id) ||
          String(o.external_option_id) === String(saved.essay.essay_option_id)
      );
      // Expanded LNAT Section B prompts share one db_id. Only the prompt
      // whose option matches the saved essay_option_id should receive the text.
      if (saved.essay.essay_option_id && essayOpts.length && !opt) return;
      map[q.id] = {
        label: opt?.label || null,
        text_response: saved.essay.response_text || '',
        essay_option_id: saved.essay.essay_option_id,
        question_db_id: qid,
      };
      return;
    }

    if (Array.isArray(saved.yes_no) && saved.yes_no.length) {
      const yn = {};
      saved.yes_no.forEach((item) => {
        if (item?.yes_no_item_id) yn[String(item.yes_no_item_id)] = Boolean(item.answered_yes);
      });
      map[q.id] = {
        yes_no: saved.yes_no,
        yes_no_answers: yn,
        question_db_id: qid,
      };
      return;
    }

    if (saved.most_least) {
      map[q.id] = {
        most_option_id: saved.most_least.most_option_id,
        least_option_id: saved.most_least.least_option_id,
        most_least_answers: {
          most_option_id: saved.most_least.most_option_id,
          least_option_id: saved.most_least.least_option_id,
        },
        question_db_id: qid,
      };
      return;
    }

    if (saved.selected_answer_id) {
      const opt = (q.options || []).find(
        (o) => String(o.db_answer_id) === String(saved.selected_answer_id)
      );
      map[q.id] = {
        label: opt?.label || saved.selected_answer_letter || null,
        answer_id: saved.selected_answer_id,
        question_db_id: qid,
      };
    }
  });

  return map;
}

/** Map DB paper statuses into TMUA local score/status shape. */
export function mergeDbStatusesIntoTmuaScores(dbPapers = {}, existingScores = {}) {
  const next = { ...existingScores };
  Object.entries(dbPapers || {}).forEach(([paperId, row]) => {
    if (!row || row.status === 'not_started') return;
    const prev = next[String(paperId)] || {};
    const answered = Number(row.score_answered) || 0;
    const paperTotal = Number(row.score_total) || 0;
    // TARA set papers often have no quiz_questions.paper_id, so score_total is
    // 0 even when answers were saved. Treat answered marks as scored too, or
    // the merge keeps correct as null and the hub renders —.
    const scored = paperTotal > 0 || answered > 0;
    next[String(paperId)] = {
      ...prev,
      correct: scored ? Number(row.score_correct) || 0 : (prev.correct ?? null),
      // Actual answers saved (pause mid-paper => partial). Overview uses this.
      answered,
      // Full paper size for grade / table raw (correct/total).
      total: paperTotal || prev.total || answered,
      mode: row.attempt_mode || prev.mode || null,
      dbStatus: row.status,
      attemptId: row.attempt_id || null,
      updatedAt: Date.now(),
    };
  });
  return next;
}

/**
 * The scores a workspace row shows, by usePaperAttemptStatuses' `state`:
 * the server's answer once it has one (`ready`), the signed-in user's cached
 * copy while it is on its way (`pending`), and nothing otherwise (signed
 * out, no ids, or the request failed), so a failure shows a neutral row.
 * `loadCached` is called only while pending.
 */
export function scoresFromStatuses(state, dbPapers = {}, loadCached = () => ({})) {
  if (state === 'ready') return mergeDbStatusesIntoTmuaScores(dbPapers, {});
  if (state === 'pending') return loadCached() || {};
  return {};
}

/** Ids whose latest attempt the server reports completed. */
export function completedIdsFromStatuses(dbPapers = {}) {
  return Object.entries(dbPapers || {})
    .filter(([, row]) => row?.status === 'completed')
    .map(([paperId]) => paperId);
}

const isCompletedRow = (row) => row?.status === 'completed';

/**
 * Copy the server's scores into the user's cache, so the next visit shows
 * them before the server answers. `shouldCache(row)` picks the rows. The
 * cache is a copy: a storage that refuses the write (full, or blocked) is
 * logged and the page carries on with the server's answer.
 */
export function cacheServerScores(dbPapers, save, shouldCache = isCompletedRow) {
  try {
    Object.entries(dbPapers || {}).forEach(([paperId, row]) => {
      if (!shouldCache(row)) return;
      save(paperId, {
        correct: row.score_correct,
        total: Number(row.score_total) || Number(row.score_answered) || 0,
        mode: row.attempt_mode || null,
      });
    });
  } catch (err) {
    console.warn('paper score cache write failed', err?.name || null);
  }
}

export function sittingStatusFromDbPapers(paper1Id, paper2Id, dbPapers = {}) {
  const s1 = paper1Id ? dbPapers[String(paper1Id)]?.status : null;
  const s2 = paper2Id ? dbPapers[String(paper2Id)]?.status : null;
  const statuses = [s1, s2].filter(Boolean);
  if (!statuses.length) return 'none';
  if (statuses.every((s) => s === 'completed')) return 'done';
  if (statuses.some((s) => s === 'in_progress' || s === 'completed')) return 'progress';
  return 'none';
}
